import { CourseType } from '@/types/course';

type SectionSelectionMode = 'NO_PREFERENCE' | 'SECTION_SELECTED';

export const extractComparableCourseCode = (code: number) => {
  return code.toString().slice(0, 8);
};

const koreanSectionMap = [
  '가',
  '나',
  '다',
  '라',
  '마',
  '바',
  '사',
  '아',
  '자',
  '차',
  '카',
  '타',
  '파',
  '하',
];

const SECTION_CODE_LENGTH = 2;
const MAX_CODE_BASED_KOREAN_SECTION_COUNT = 9;

const formatSectionNumberFallback = (sectionNumber: number) => {
  return `${sectionNumber.toString().padStart(2, '0')}분반`;
};

const getSectionNumberFromCode = (code: number) => {
  return Number(code.toString().slice(-SECTION_CODE_LENGTH).padStart(SECTION_CODE_LENGTH, '0'));
};

const formatSectionLabelFromIndex = (index: number) => {
  const sectionNumber = index + 1;

  return koreanSectionMap[index] ?? formatSectionNumberFallback(sectionNumber);
};

export const formatSectionLabelFromCode = (code: number, orderedSectionCodes: number[]) => {
  const sectionNumber = getSectionNumberFromCode(code);

  if (orderedSectionCodes.length <= MAX_CODE_BASED_KOREAN_SECTION_COUNT) {
    if (sectionNumber >= 1 && sectionNumber <= MAX_CODE_BASED_KOREAN_SECTION_COUNT) {
      return koreanSectionMap[sectionNumber - 1] ?? formatSectionNumberFallback(sectionNumber);
    }

    return formatSectionNumberFallback(sectionNumber);
  }

  const orderedIndex = orderedSectionCodes.indexOf(code);

  if (orderedIndex >= 0) {
    return formatSectionLabelFromIndex(orderedIndex);
  }

  return formatSectionNumberFallback(sectionNumber);
};

export const groupCoursesByComparableCode = <TCourse extends Pick<CourseType, 'code'>>(
  courses: TCourse[],
) => {
  return courses.reduce<Record<string, TCourse[]>>((acc, course) => {
    const comparableCode = extractComparableCourseCode(course.code);
    if (!acc[comparableCode]) {
      acc[comparableCode] = [];
    }
    acc[comparableCode].push(course);
    return acc;
  }, {});
};

export const countUniqueLateCourses = (courses: Array<Pick<CourseType, 'code' | 'timing'>>) => {
  const lateComparableCodes = new Set(
    courses
      .filter((course) => course.timing === 'LATE')
      .map((course) => extractComparableCourseCode(course.code)),
  );

  return lateComparableCodes.size;
};

export const hasAnySingleSectionStrictRestrictionCourse = <
  TCourse extends Pick<CourseType, 'code'> & { isStrictRestriction?: boolean },
>(
  courses: TCourse[],
) => {
  const groupedCourses = groupCoursesByComparableCode(courses);

  return Object.values(groupedCourses).some(
    (sectionCourses) =>
      sectionCourses.length === 1 && Boolean(sectionCourses[0]?.isStrictRestriction),
  );
};

export const isSameCourse = (a: CourseType, b: CourseType) => {
  return extractComparableCourseCode(a.code) === extractComparableCourseCode(b.code);
};

export const isSameCourseCode = (a: number, b: number) => {
  return extractComparableCourseCode(a) === extractComparableCourseCode(b);
};

export const shouldShowExplicitSectionInfo = ({
  sectionCount,
  selectedSectionCount,
  selectionMode,
}: {
  sectionCount: number;
  selectedSectionCount: number;
  selectionMode?: null | SectionSelectionMode;
}) => {
  return sectionCount >= 2 && selectedSectionCount > 0 && selectionMode === 'SECTION_SELECTED';
};

export const formatSectionLabelSuffix = (codes: number[], orderedSectionCodes: number[]) => {
  if (codes.length === 0) {
    return '';
  }

  const selectedCodeSet = new Set(codes);
  const orderedSelectedCodes = orderedSectionCodes.filter((code) => selectedCodeSet.has(code));
  const missingCodes = codes.filter((code) => !orderedSelectedCodes.includes(code));
  const labelTargetCodes = [...orderedSelectedCodes, ...missingCodes];
  const sectionLabels = labelTargetCodes.map((code) =>
    formatSectionLabelFromCode(code, orderedSectionCodes),
  );

  if (sectionLabels.length > 4) {
    const visibleLabels = sectionLabels.slice(0, 4);
    const remainingCount = sectionLabels.length - visibleLabels.length;

    return `(${visibleLabels.join(', ')} 외 ${remainingCount}개)`;
  }

  return `(${sectionLabels.join(', ')})`;
};

export const formatSectionScheduleTokens = (scheduleRoom?: null | string) => {
  if (!scheduleRoom) {
    return [];
  }

  return scheduleRoom
    .split('\n')
    .flatMap((line) => line.split(','))
    .map((slot) => slot.split('(')[0]?.trim() ?? '')
    .map((slot) => slot.replace('-', ' ~ '))
    .filter(Boolean);
};

export const formatSectionScheduleLine = (scheduleRoom?: null | string) => {
  const timeTokens = formatSectionScheduleTokens(scheduleRoom);

  return timeTokens.join(', ');
};

export const formatProfessorNames = (professors: string[]) => {
  if (professors.length >= 4) {
    const [firstProfessor, secondProfessor] = professors;
    const remainingProfessorCount = professors.length - 2;
    return `${firstProfessor}, ${secondProfessor} 외 ${remainingProfessorCount}명`;
  }

  return professors.join(', ');
};

export const formatProfessorLine = (professors: string[]) => {
  if (professors.length === 0) {
    return '담당 교수님 미정';
  }

  return `${formatProfessorNames(professors)} 교수님`;
};
